/**
 * Reading state data (registry.ts). Every module that keeps a per-state table or reads a state's pack imports from here,
 * so the eager install (eager.ts: all states for the server, scripts, tests and the phone app; nothing in the web build)
 * has always run first.
 */
import "./eager.ts";
import { loadedData } from "./registry.ts";
import { SUPPORTED_STATES, type UsState } from "./types.ts";

export * from "./registry.ts";

/** A namespace of a loaded state's bundle (a whole module's exports), typed by the caller from the module itself. */
export function stateModule<M>(state: UsState, ns: string): M {
  const m = loadedData(state, ns)[ns];
  if (!m) throw new Error(`The ${state} bundle has no "${ns}" (regenerate it: bun scripts/states/gen-bundles.ts)`);
  return m as unknown as M;
}

/** Every key a `perStateTable` was made for, so a test can prove each bundle fills each one. */
export const TABLE_KEYS: string[] = [];

/**
 * A module's per-state table, `{ UT, NV, ... }`, as the modules always wrote it, but only Utah's words are in the module:
 * a read of another state's entry takes it from the registry (and throws StateNotLoadedError when that state is not
 * loaded). `key` is the export name without its state suffix (`LIBRARY_NV` -> "LIBRARY").
 */
export function perStateTable<W>(key: string, ut: W): Record<UsState, W> {
  if (!TABLE_KEYS.includes(key)) TABLE_KEYS.push(key);
  return perStateFrom<W, Record<string, W>>("words", (m) => {
    if (!(key in m)) throw new Error(`The state bundle has no words "${key}" (regenerate: bun scripts/states/gen-bundles.ts)`);
    return m[key]!;
  }, ut);
}

/** Like perStateTable for any module of the bundle: `pick` takes the value out of that state's namespace `ns`. */
export function perStateFrom<T, M = Record<string, unknown>>(ns: string, pick: (module: M, state: UsState) => T, ut: T): Record<UsState, T> {
  const table = {} as Record<UsState, T>;
  for (const st of SUPPORTED_STATES) {
    Object.defineProperty(table, st, { enumerable: true, get: st === "UT" ? () => ut : () => pick(stateModule<M>(st, ns), st) });
  }
  return table;
}
